import { CommonModule as ACommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EmailLinkPipe } from '@tytapp/common/emailLink.pipe';

import { AutolinkPipe } from './autolink.pipe';
import { Base64 } from './base64';
import { Cookies } from './cookies';
import { DateAsUTCDirective } from './date-as-utc.directive';
import { DialogGuard } from './dialog-guard';
import { FormHandler } from './form-handler';
import { FormComponent } from './form/form.component';
import { Head } from './head';
import { NetworkInformation } from './network-information';
import { OpenGraph } from './opengraph';
import { RequestParams } from './request-params';
import { ResponsiveBackgroundImageDirective } from './responsive-background-image.directive';
import { SectionLinkComponent } from './section-link/section-link.component';
import { Shell } from './shell';
import { SoftwareKeyboard } from './software-keyboard';
import { SpinnerComponent } from './spinner/spinner.component';
import { StagingNoteComponent } from './staging-note/staging-note.component';
import { UserAgent } from './user-agent';
import { ViewportSize } from './viewport-size';

import { AppConfig } from './app-config';
import { AsDatePipe } from './as-date.pipe';
import { DurationPipe } from './duration.pipe';
import { FormSubmitButtonComponent } from './form-submit-button/form-submit-button.component';
import { LoggerService } from './logger.service';
import { MarkdownToHtmlPipe } from './markdown-to-html.pipe';
import { Redirection } from './redirection';
import { Retry } from './retry';
import { TrustHtmlPipe } from './trust-html.pipe';

import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FormGetDirective } from '@tytapp/common/form-get/form-get.directive';
import { HashElementsService } from '@tytapp/common/hash-elements.service';
import { ImageService } from '@tytapp/common/image-service';
import { IsoDatePipe } from '@tytapp/common/iso-date.pipe';
import { LinksToRouterDirective } from '@tytapp/common/links-to-router.directive';
import { TrustUrlPipe } from '@tytapp/common/trust-url.pipe';
import { UniversalLinkDirective } from '@tytapp/common/universal-link.directive';
import { DateTimePickerComponent } from '../common-ui/date-time-picker/date-time-picker.component';
import { TimeValueAccessor } from '../common-ui/date-time-picker/time-value-accessor';
import { AutosizeDirective } from './autosize.directive';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { IntersectionObserverComponent } from './intersection-observer.component';
import { LinksToHtmlPipe } from './links-to-html.pipe';
import { MessageDialogComponent } from './message-dialog/message-dialog.component';
import { NewlinesToHtmlPipe } from './newlines-to-html.pipe';
import { NoticeService } from './notice-service';
import { NoticeComponent } from './notice/notice.component';
import { PageContextComponent } from './page-context.component';
import { RepeatPipe } from './repeat.pipe';
import { ResponsivePictureComponent } from './responsive-picture.component';
import { ShortNumberPipe } from './short-number.pipe';
import { SubMenuItemComponent } from './sub-menu-item.component';
import { SummarizePipe } from './summarize.pipe';
import { TimestampComponent } from './timestamp.component';
import { LoadGuardComponent } from './load-guard/load-guard.component';


const COMPONENTS = [
    SectionLinkComponent,
    FormComponent,
    SpinnerComponent,
    LoadGuardComponent,
    StagingNoteComponent,
    ResponsiveBackgroundImageDirective,
    DateAsUTCDirective,
    AutolinkPipe,
    DurationPipe,
    AsDatePipe,
    TrustHtmlPipe,
    TrustUrlPipe,
    MarkdownToHtmlPipe,
    IsoDatePipe,
    NewlinesToHtmlPipe,
    ShortNumberPipe,
    SummarizePipe,
    LinksToHtmlPipe,
    FormSubmitButtonComponent,
    NoticeComponent,
    ResponsivePictureComponent,
    EmailLinkPipe,
    PageContextComponent,
    TimestampComponent,
    TimeValueAccessor,
    DateTimePickerComponent,
    MessageDialogComponent,
    AutosizeDirective,
    SubMenuItemComponent,
    ConfirmationDialogComponent,
    MessageDialogComponent,
    IntersectionObserverComponent,
    LinksToRouterDirective,
    UniversalLinkDirective,
    FormGetDirective,
    RepeatPipe
];

@NgModule({
    imports: [
        HttpClientModule,
        FormsModule,
        RouterModule,
        ACommonModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatDatepickerModule
    ],
    declarations: COMPONENTS,
    exports: COMPONENTS
})
export class TYTCommonModule {
    static forRoot(): ModuleWithProviders<TYTCommonModule> {
        return {
            ngModule: TYTCommonModule,
            providers: [
                Shell,
                NetworkInformation,
                Cookies,
                Base64,
                UserAgent,
                FormHandler,
                SoftwareKeyboard,
                RequestParams,
                OpenGraph,
                Head,
                AppConfig,
                ViewportSize,
                DialogGuard,
                Redirection,
                LoggerService,
                NoticeService,
                ImageService,
                Retry,
                HashElementsService,
                {
                    provide: APP_INITIALIZER,
                    useFactory: () => () => {},
                    deps: [HashElementsService],
                    multi: true
                }
            ]
        };
    }
}
